import { useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material';
import posthog from 'posthog-js';

import { useColorScheme } from 'hooks/useColorScheme';
import { useKeyboardShortcuts } from 'hooks/useKeyboardShortcuts/useKeyboardShortcuts';
import { useReleaseFlag, useReleaseFlagsLoading } from 'hooks/useReleaseFlags';

/**
 * Component that initializes theme-related functionality, including keyboard shortcuts.
 * Must be rendered within ThemeProvider to access color scheme.
 * Dark mode is gated by the `dark_mode` release flag.
 */
export const ThemeInitializer = (): null => {
  const { isDarkMode, mode, setMode } = useColorScheme();
  const theme = useTheme();
  const flagsLoading = useReleaseFlagsLoading();
  const isDarkModeEnabled = useReleaseFlag('dark_mode');

  // Force light mode when the dark_mode flag is disabled
  useEffect(() => {
    if (flagsLoading) return;
    if (!isDarkModeEnabled && mode !== 'light') {
      setMode('light');
    }
  }, [flagsLoading, isDarkModeEnabled, mode, setMode]);

  // Set data-theme attribute on document for CSS styling (needed for mapbox)
  useEffect(() => {
    if (mode) {
      document.documentElement.setAttribute('data-theme', mode);
    }
  }, [mode]);

  // Set CSS variables for theme-appropriate styling of react-spring-bottom-sheet.
  // Use `background.paper` so sheets match MUI Drawer / panel surfaces (not `default`, which is the app canvas behind the map).
  useEffect(() => {
    if (theme.palette?.background?.paper) {
      document.documentElement.style.setProperty(
        '--rsbs-bg',
        theme.palette.background.paper,
      );
    }
    if (theme.palette?.grey && mode) {
      document.documentElement.style.setProperty(
        '--rsbs-handle-bg',
        theme.palette.grey[isDarkMode ? 600 : 400],
      );
    }
  }, [isDarkMode, mode, theme.palette]);

  // Track theme properties with PostHog as super properties
  useEffect(() => {
    if (flagsLoading || !isDarkModeEnabled) {
      return;
    }

    posthog.register({
      themePreference: mode,
      themeResolved: isDarkMode ? 'dark' : 'light',
    });
  }, [flagsLoading, isDarkModeEnabled, mode, isDarkMode]);

  const toggleMode = useCallback(() => {
    if (flagsLoading) return;
    if (isDarkModeEnabled) {
      setMode(isDarkMode ? 'light' : 'dark');
    }
  }, [flagsLoading, isDarkModeEnabled, isDarkMode, setMode]);

  useKeyboardShortcuts(
    [
      {
        category: 'common',
        description: 'Toggle dark mode',
        keys: 'd',
        handler: toggleMode,
      },
    ],
    [toggleMode],
  );

  return null;
};
